import { type Difficulty, normalizeRoomCode, ROOM_CODE_LENGTH } from "@playpal/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { AuthScreen } from "./components/AuthScreen";
import { HomeScreen } from "./components/HomeScreen";
import { ProfileScreen } from "./components/ProfileScreen";
import { RoomScreen } from "./components/RoomScreen";
import { ApiError, api } from "./lib/api";
import { configurationProblem } from "./lib/config";
import { type Identity, loadIdentity } from "./lib/identity";
import { useNativeShell } from "./lib/nativeShell";
import { useSession } from "./lib/session";
import { useRoom } from "./lib/useRoom";

type Screen = "home" | "auth" | "profile";

function codeFromUrl(): string | null {
	const raw = new URLSearchParams(window.location.search).get("room");
	if (!raw) return null;
	const code = normalizeRoomCode(raw);
	return code.length === ROOM_CODE_LENGTH ? code : null;
}

/**
 * The code to start on — which is not always the one in the address bar.
 *
 * Adding a page to the home screen on iOS ignores the manifest's `start_url`
 * and bakes in whatever URL is showing at the time. Do that during a game and
 * the icon points at `?room=SZRV` for ever: every launch, weeks later, opens a
 * room that closed the same afternoon, shows "Can't reach that game", and
 * gives a nine-year-old an app that appears broken every time they open it.
 *
 * `navigator.standalone` is iOS-only and true only for a home-screen launch,
 * which is exactly the case where a room code in the URL cannot have come from
 * a friend — iOS opens tapped links in Safari, not in the installed app. On
 * Android, where Chrome does hand in-scope links to the installed app, this is
 * `undefined` and a shared link still works.
 */
function initialCode(): string | null {
	const fromIcon = (navigator as { standalone?: boolean }).standalone === true;
	return fromIcon ? null : codeFromUrl();
}

/**
 * Keeps the room code in the address bar, and — the part that matters — makes
 * entering a room a history entry so the browser's back button leaves it.
 *
 * It used to replace the entry instead, which meant back exited the site
 * altogether even though the URL had visibly changed. That is a surprising way
 * to lose a game, and on a phone the back gesture is how you go back.
 */
function rememberCodeInUrl(code: string | null, push: boolean): void {
	const url = new URL(window.location.href);
	if (code) url.searchParams.set("room", code);
	else url.searchParams.delete("room");

	if (push) window.history.pushState({ room: code }, "", url);
	else window.history.replaceState({ room: code }, "", url);
}

export function App() {
	const [identity, setIdentity] = useState<Identity>(loadIdentity);
	const [code, setCode] = useState<string | null>(initialCode);
	const [screen, setScreen] = useState<Screen>("home");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(configurationProblem);

	const session = useSession(identity, setIdentity);
	const connection = useRoom(code, identity, session.token);

	// Entering a room pushes; leaving replaces, because leaving is usually the
	// back button itself and pushing there would need two presses to escape.
	const previousCode = useRef<string | null>(code);
	// Whether there is an entry behind this room for `leave` to go back to.
	// Landing straight on a shared link, there is not.
	const pushedEntry = useRef(false);
	useEffect(() => {
		const entering = code !== null && previousCode.current === null;
		rememberCodeInUrl(code, entering);
		if (entering) pushedEntry.current = true;
		if (code === null) pushedEntry.current = false;
		previousCode.current = code;
	}, [code]);

	// Back out of a room rather than out of PlayPal.
	useEffect(() => {
		const onPop = (event: PopStateEvent) => {
			const state = event.state as { room?: string | null } | null;
			setCode(state?.room ?? codeFromUrl());
		};
		window.addEventListener("popstate", onPop);
		return () => window.removeEventListener("popstate", onPop);
	}, []);

	// A room that has gone (or is full) drops us back to the front page.
	useEffect(() => {
		if (connection.fatal) {
			setError(connection.fatal);
			setCode(null);
		}
	}, [connection.fatal]);

	const openRoom = useCallback(
		async (options: { mode: "friend" | "computer"; difficulty?: Difficulty }) => {
			setBusy(true);
			setError(null);
			try {
				const room = await api.createRoom(options);
				setCode(room.code);
				setScreen("home");
			} catch (caught) {
				setError(
					caught instanceof ApiError
						? caught.message
						: "Couldn't start a game. Check the internet and try again.",
				);
			} finally {
				setBusy(false);
			}
		},
		[],
	);

	const joinRoom = useCallback((raw: string) => {
		const next = normalizeRoomCode(raw);
		if (next.length !== ROOM_CODE_LENGTH) {
			setError("A code is four letters, like BDFG.");
			return;
		}
		setError(null);
		setCode(next);
		setScreen("home");
	}, []);

	const leave = useCallback(() => {
		setError(null);
		/*
		 * Leaving has to work on its own.
		 *
		 * This used to be `history.back()` and nothing else, chosen because
		 * `history.state.room` was set — which it always is, including on the
		 * very first entry. Tap the link a friend sent you and that room is the
		 * only entry in the tab: there is nothing behind it, `back()` is
		 * silently a no-op, no popstate fires, and the code is never cleared.
		 * Leave did nothing, every time you pressed it. A child who joined the
		 * way you are supposed to join could not get out of the room at all.
		 *
		 * So clear it here. Going back is now only the tidy-up, and only when
		 * we know we put an entry there to go back to.
		 */
		setCode(null);
		if (pushedEntry.current) window.history.back();
	}, []);

	// Android's back button leaves the room first, and only then the app.
	useNativeShell(() => {
		if (code) {
			leave();
			return true;
		}
		if (screen !== "home") {
			setScreen("home");
			return true;
		}
		return false;
	});

	if (code) {
		return <RoomScreen connection={connection} onLeave={leave} />;
	}

	if (screen === "auth") {
		return (
			<AuthScreen
				session={session}
				identity={identity}
				onDone={() => setScreen("home")}
				onCancel={() => setScreen("home")}
			/>
		);
	}

	if (screen === "profile" && session.signedIn) {
		return (
			<ProfileScreen
				session={session}
				onBack={() => setScreen("home")}
				// "Play again" opens a fresh room to share with that friend.
				onInvite={() => void openRoom({ mode: "friend" })}
			/>
		);
	}

	return (
		<HomeScreen
			identity={identity}
			session={session}
			onIdentityChange={setIdentity}
			onCreate={() => void openRoom({ mode: "friend" })}
			onPlayComputer={(difficulty) => void openRoom({ mode: "computer", difficulty })}
			onJoin={joinRoom}
			onOpenAuth={() => setScreen("auth")}
			onOpenProfile={() => setScreen("profile")}
			busy={busy}
			error={error}
		/>
	);
}
